import React, { FC, useEffect, useState } from 'react';
import { Button, Modal, Select } from 'flowbite-react';
import { INotification } from '../../common/interfaces/NotificationConfiguration.interface';
import { t } from 'i18next';
import { IMultiLanguageValue } from '../../common/interfaces/MultiLanguageValue.interface';
import { Status } from '../../common/enums/Status';
import { Priority } from '../../common/enums/Priority';
import { NotificationCode } from '../../common/enums/NotificationCode';
import { MultiLanguageFieldGroup } from '../Shared/MultiLanguageInput';
import { EditRadioGroup } from '../Shared/EditRadioGroup';
import { EditTextInput } from '../Shared/EditTextInput';

interface NotificationEditModalProps {
  isOpen: boolean;
  onClose: () => void;
  notification: INotification | null;
  onSave: (updatedNotification: INotification) => void;
  mode: 'edit' | 'create';
}

export const NotificationEditModal: FC<NotificationEditModalProps> = ({
  isOpen,
  onClose,
  notification,
  onSave,
  mode,
}) => {
  const [isSaving, setIsSaving] = useState(false);
  const [titles, setTitles] = useState<IMultiLanguageValue[]>([]);
  const [body, setBody] = useState<IMultiLanguageValue[]>([]);
  const [status, setStatus] = useState<Status | null>(null);
  const [deepLink, setDeepLink] = useState<string>('');
  const [screenName, setScreenName] = useState<string>('');
  const [priority, setPriority] = useState<Priority | null>(null);
  const [selectedCode, setSelectedCode] = useState<NotificationCode | null>(
    null
  );

  useEffect(() => {
    if (mode === 'edit' && notification) {
      setTitles(notification.title || []);
      setBody(notification.body || []);
      setStatus(notification.status || null);
      setDeepLink(notification.deepLink || '');
      setScreenName(notification.screenName || '');
      setPriority(notification.priority || null);
    } else if (mode === 'create') {
      // Reset fields for creating a new notification
      setTitles([]);
      setBody([]);
      setStatus(null);
      setDeepLink('');
      setScreenName('');
      setPriority(null);
    }
  }, [notification, mode]);

  const handleSave = () => {
    if (isSaving) return;
    setIsSaving(true);
    const updatedNotification: INotification = {
      id: notification?.id || '',
      code:
        selectedCode ||
        notification?.code ||
        NotificationCode.CONTEXT_CONTENT_REMINDER,
      title: titles,
      body,
      status: status || notification?.status || Status.INACTIVE,
      deepLink,
      screenName,
      priority: priority || notification?.priority || Priority.high,
    };
    onSave(updatedNotification);
    setTimeout(() => setIsSaving(false), 500);
  };
  const disabledButton = !(
    titles.length > 0 &&
    body.length > 0 &&
    status &&
    priority &&
    screenName.trim() &&
    deepLink.trim()
  );

  return (
    <Modal show={isOpen} onClose={onClose}>
      <Modal.Header className="modal-header">
        {mode === 'edit'
          ? t('notification.editNotification')
          : t('notification.addNotification')}
      </Modal.Header>
      <Modal.Body>
        <MultiLanguageFieldGroup
          fields={[
            {
              id: 'title',
              label: t('notification.title'),
              type: 'text',
              placeholder: t('placeholders.notifyTitle'),
              required: true,
              autoFocus: true,
            },
            {
              id: 'body',
              label: t('notification.body'),
              type: 'textarea',
              placeholder: t('placeholders.notifyBody'),
              required: true,
              rows: 3,
            },
          ]}
          values={{
            title: titles,
            body,
          }}
          onChange={(fieldId, value) => {
            if (fieldId === 'title') {
              setTitles(value);
            } else if (fieldId === 'body') {
              setBody(value);
            }
          }}
          sharedLanguageSelector={true}
          cardWrapper={false}
          t={t}
          languageSelectorLabel="notification.selectNotificationLanguage"
        />

        <EditTextInput
          label={t('notification.screenName')}
          value={screenName}
          onChange={setScreenName}
          placeholder={t('notification.screenName')}
        />
        <EditTextInput
          label={t('notification.deepLink')}
          value={deepLink}
          onChange={setDeepLink}
          placeholder={t('notification.deepLink')}
        />
        <div>
          <label
            htmlFor="notificationCode"
            className="block text-sm font-bold text-gray-700 dark:text-gray-100"
          >
            {t('notification.code')}
          </label>
          {mode === 'edit' ? (
            <div className="mb-4 mt-2 rounded bg-gray-100 p-3">
              {notification?.code}
            </div>
          ) : (
            <div>
              <div>
                <Select
                  id="notificationCode"
                  value={selectedCode || ''}
                  onChange={(e) =>
                    setSelectedCode(e.target.value as NotificationCode)
                  }
                >
                  {Object.values(NotificationCode).map((codeValue) => (
                    <option key={codeValue} value={codeValue}>
                      {codeValue}
                    </option>
                  ))}
                </Select>
              </div>
            </div>
          )}
        </div>
        <EditRadioGroup
          label={t('notification.priority')}
          options={Object.values(Priority)}
          optionName="priorities"
          selectedValue={priority}
          onChange={(value) => setPriority(value as Priority)}
        />
        <EditRadioGroup
          label={t('contentContext.status')}
          options={Object.values(Status)}
          optionName="status"
          selectedValue={status}
          onChange={(value) => setStatus(value as Status)}
        />
      </Modal.Body>
      <Modal.Footer>
        <div className="flex w-full items-center">
          <Button
            gradientMonochrome="success"
            disabled={disabledButton || isSaving}
            onClick={handleSave}
          >
            {t('content.save')}
          </Button>
          <Button className="ms-auto" color="gray" onClick={onClose}>
            {t('media.close')}
          </Button>
        </div>
      </Modal.Footer>
    </Modal>
  );
};
